import { useState, useEffect, useCallback } from 'react';
import logger from '@/utils/logger';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Save, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EditYarn() {
  const { yarnId } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const loadYarn = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/yarns/${yarnId}`, {
        withCredentials: true
      });
      setFormData(response.data);
    } catch (error) {
      logger.error('Failed to load yarn:', error);
      toast.error('Failed to load yarn');
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  }, [yarnId, navigate]);

  useEffect(() => {
    loadYarn();
  }, [loadYarn]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        quantity: parseInt(formData.quantity) || 1,
        price: formData.price ? parseFloat(formData.price) : null
      };

      await axios.put(`${API}/yarns/${yarnId}`, dataToSend, {
        withCredentials: true
      });

      toast.success('Yarn updated successfully!');
      navigate(`/inventory/${yarnId}`);
    } catch (error) {
      logger.error('Failed to update yarn:', error);
      toast.error('Failed to update yarn');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !formData) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate(`/inventory/${yarnId}`)}
          className="mb-6 rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Details
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
            Edit Yarn
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="brand">Brand</Label>
              <Input
                data-testid="input-brand"
                id="brand"
                name="brand"
                value={formData.brand || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="color">Color</Label>
              <Input
                data-testid="input-color"
                id="color"
                name="color"
                value={formData.color || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="weight">Weight</Label>
              <Input
                data-testid="input-weight"
                id="weight"
                name="weight"
                value={formData.weight || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="fiber_content">Fiber Content</Label>
              <Input
                data-testid="input-fiber"
                id="fiber_content"
                name="fiber_content"
                value={formData.fiber_content || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="yardage">Yardage</Label>
              <Input
                data-testid="input-yardage"
                id="yardage"
                name="yardage"
                value={formData.yardage || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                data-testid="input-quantity"
                id="quantity"
                name="quantity"
                type="number"
                min="1"
                value={formData.quantity || 1}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="hook_size">Hook/Needle Size</Label>
              <Input
                data-testid="input-hook-size"
                id="hook_size"
                name="hook_size"
                value={formData.hook_size || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="gauge">Gauge</Label>
              <Input
                data-testid="input-gauge"
                id="gauge"
                name="gauge"
                value={formData.gauge || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="dye_lot">Dye Lot</Label>
              <Input
                data-testid="input-dye-lot"
                id="dye_lot"
                name="dye_lot"
                value={formData.dye_lot || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="purchase_date">Purchase Date</Label>
              <Input
                data-testid="input-purchase-date"
                id="purchase_date"
                name="purchase_date"
                type="date"
                value={formData.purchase_date || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                data-testid="input-location"
                id="location"
                name="location"
                value={formData.location || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="price">Price</Label>
              <Input
                data-testid="input-price"
                id="price"
                name="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price || ''}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-4">
            <Button
              data-testid="cancel-button"
              type="button"
              variant="ghost"
              onClick={() => navigate(`/inventory/${yarnId}`)}
              className="rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
            >
              Cancel
            </Button>
            <Button
              data-testid="save-button"
              type="submit"
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
